/**
 * ProceduresMemberService
 * Signed-in portal surface: the caller's directory (assigned private + public), procedures, runs and AI chat.
 * Route: procedures/api/procedures/me (any signed-in user; per-user rate limit `procedures-member`)
 *
 * Signed-in runs need no run token. A procedure the caller is not assigned to answers 403 `not_assigned`.
 */
import { BaseService } from '../base/BaseService';
import { unwrapProceduresResponse as unwrap } from './unwrapProceduresResponse';
/** Member-facing Procedures service (JWT). */
export class ProceduresMemberService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? ProceduresMemberService.DEFAULT_SERVICE_PATH,
            basePath: ProceduresMemberService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    enc(value) {
        return encodeURIComponent(value);
    }
    // ---- directory ----
    /** GET api/procedures/me/directory: assigned private procedures plus the public directory. */
    async getMemberDirectory(query = {}) {
        return unwrap(await this.getMemberDirectoryRaw(query), 'Procedures directory not found');
    }
    getMemberDirectoryRaw(query = {}) {
        return this.http.get(this.buildUrlWithParams('directory', {
            search: query.search,
            categoryID: query.categoryID,
            locationID: query.locationID,
            page: query.page,
            pageSize: query.pageSize,
        }));
    }
    /** GET api/procedures/me/categories */
    async getMemberCategories() {
        return (await this.getMemberCategoriesRaw()).data ?? [];
    }
    getMemberCategoriesRaw() {
        return this.http.get(this.buildUrl('categories'));
    }
    /** GET api/procedures/me/procedures/{code}: published snapshot; `resumeRunID` is set when an unfinished run exists. */
    async getMemberProcedure(code) {
        return unwrap(await this.getMemberProcedureRaw(code), `Procedure ${code} not found`);
    }
    getMemberProcedureRaw(code) {
        return this.http.get(this.buildUrl(`procedures/${this.enc(code)}`));
    }
    // ---- runs ----
    /** POST api/procedures/me/procedures/{code}/runs: start a signed-in run (no run token is returned). */
    async startMemberRun(code, request = {}) {
        return unwrap(await this.startMemberRunRaw(code, request), 'Failed to start the procedure');
    }
    startMemberRunRaw(code, request = {}) {
        return this.http.post(this.buildUrl(`procedures/${this.enc(code)}/runs`), request);
    }
    /** GET api/procedures/me/runs: the caller's runs, newest first. */
    async getMyRuns(query = {}) {
        return unwrap(await this.getMyRunsRaw(query), 'Runs not found');
    }
    getMyRunsRaw(query = {}) {
        return this.http.get(this.buildUrlWithParams('runs', { page: query.page, pageSize: query.pageSize }));
    }
    /** GET api/procedures/me/runs/{runId} */
    async getMemberRun(runId) {
        return unwrap(await this.getMemberRunRaw(runId), `Run ${runId} not found`);
    }
    getMemberRunRaw(runId) {
        return this.http.get(this.buildUrl(`runs/${runId}`));
    }
    /** PUT api/procedures/me/runs/{runId}/steps/{stepId}: tick, skip, report a problem or untick. */
    async updateMemberRunStep(runId, stepId, request) {
        return unwrap(await this.updateMemberRunStepRaw(runId, stepId, request), 'Failed to update the step');
    }
    updateMemberRunStepRaw(runId, stepId, request) {
        return this.http.put(this.buildUrl(`runs/${runId}/steps/${stepId}`), request);
    }
    /** POST api/procedures/me/runs/{runId}/complete */
    async completeMemberRun(runId, request = {}) {
        return unwrap(await this.completeMemberRunRaw(runId, request), 'Failed to complete the run');
    }
    completeMemberRunRaw(runId, request = {}) {
        return this.http.post(this.buildUrl(`runs/${runId}/complete`), request);
    }
    /** POST api/procedures/me/runs/{runId}/abandon */
    async abandonMemberRun(runId, request = {}) {
        return unwrap(await this.abandonMemberRunRaw(runId, request), 'Failed to abandon the run');
    }
    abandonMemberRunRaw(runId, request = {}) {
        return this.http.post(this.buildUrl(`runs/${runId}/abandon`), request);
    }
    // ---- AI chat ----
    /** POST api/procedures/me/procedures/{code}/chat: ask about this procedure (public + private documents). */
    async askMember(code, request) {
        return unwrap(await this.askMemberRaw(code, request), 'No answer returned');
    }
    askMemberRaw(code, request) {
        return this.http.post(this.buildUrl(`procedures/${this.enc(code)}/chat`), request);
    }
    /** GET api/procedures/me/procedures/{code}/chat/{sessionKey}: the caller's chat history for a session. */
    async getMemberChatHistory(code, sessionKey) {
        return (await this.getMemberChatHistoryRaw(code, sessionKey)).data ?? [];
    }
    getMemberChatHistoryRaw(code, sessionKey) {
        return this.http.get(this.buildUrl(`procedures/${this.enc(code)}/chat/${this.enc(sessionKey)}`));
    }
}
ProceduresMemberService.DEFAULT_SERVICE_PATH = 'procedures';
ProceduresMemberService.BASE_PATH = 'api/procedures/me';
//# sourceMappingURL=ProceduresMemberService.js.map